/**
 * RelatedPagesSection - Internal linking component for SEO authority flow
 * Adds contextual cross-links to boost crawl depth and PageRank distribution
 * Auto-translates links by href lookup across 16 languages
 */
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContextNew';
import { getTranslatedCountrySlug, type CountryKey } from '@/data/country-slug-translations';

// Map English country slug → country key for translation lookup
const COUNTRY_SLUG_TO_KEY: Record<string, CountryKey> = {
  'call-center-for-france': 'france', 'call-center-for-germany': 'germany',
  'call-center-for-spain': 'spain', 'call-center-for-uk': 'uk',
  'call-center-for-usa': 'usa', 'call-center-for-canada': 'canada',
  'call-center-for-netherlands': 'netherlands', 'call-center-for-belgium': 'belgium',
  'call-center-for-switzerland': 'switzerland', 'call-center-for-italy': 'italy',
  'call-center-for-uae': 'uae', 'call-center-for-saudi-arabia': 'saudi-arabia',
  'call-center-for-qatar': 'qatar', 'call-center-for-scandinavia': 'scandinavia',
  'call-center-for-portugal': 'portugal', 'call-center-for-japan': 'japan',
  'call-center-for-korea': 'korea', 'call-center-for-turkey': 'turkey',
  'call-center-for-brazil': 'brazil', 'call-center-for-india': 'india',
  'call-center-for-australia': 'australia', 'call-center-for-south-africa': 'south-africa',
  'call-center-for-mexico': 'mexico', 'call-center-for-colombia': 'colombia',
  'call-center-for-poland': 'poland', 'call-center-for-philippines': 'philippines',
  'call-center-for-nigeria': 'nigeria', 'call-center-for-egypt': 'egypt',
};

/** Translate a country page href to the localized slug for the given language */
function translateCountryHref(href: string, lang: string): string {
  // href looks like '/call-center-for-france'
  const slug = href.replace(/^\//, '');
  const countryKey = COUNTRY_SLUG_TO_KEY[slug];
  if (countryKey && lang !== 'en') {
    return '/' + getTranslatedCountrySlug(countryKey, lang);
  }
  return href;
}

interface RelatedLink {
  href: string;
  label: string;
  description?: string;
}

interface Props {
  links?: RelatedLink[];
  variant?: 'compact' | 'cards';
}

// Master dictionary: every href → localized label+description for all 16 languages
const linkDict: Record<string, Record<string, { label: string; description: string }>> = {
  '/services/bpo': {
    en: { label: 'BPO Contact Center', description: '24/7 multilingual support with 12+ languages' },
    fr: { label: 'Centre de Contact BPO', description: 'Support multilingue 24/7 en 12+ langues' },
    es: { label: 'Centro de Contacto BPO', description: 'Soporte multilingüe 24/7 en 12+ idiomas' },
    ar: { label: 'مركز اتصال BPO', description: 'دعم متعدد اللغات 24/7 بأكثر من 12 لغة' },
    de: { label: 'BPO Contact Center', description: 'Mehrsprachiger 24/7-Support in 12+ Sprachen' },
    it: { label: 'Contact Center BPO', description: 'Supporto multilingue 24/7 in 12+ lingue' },
    pt: { label: 'Contact Center BPO', description: 'Suporte multilíngue 24/7 em 12+ idiomas' },
    nl: { label: 'BPO Contactcenter', description: 'Meertalige 24/7 ondersteuning in 12+ talen' },
    pl: { label: 'Centrum Kontaktowe BPO', description: 'Wsparcie wielojęzyczne 24/7 w 12+ językach' },
    ru: { label: 'Контакт-центр BPO', description: 'Мультиязычная поддержка 24/7 на 12+ языках' },
    zh: { label: 'BPO联络中心', description: '12+种语言的全天候多语支持' },
    ja: { label: 'BPOコンタクトセンター', description: '12+言語の24時間多言語サポート' },
    ko: { label: 'BPO 컨택센터', description: '12+ 언어로 24/7 다국어 지원' },
    tr: { label: 'BPO İletişim Merkezi', description: '12+ dilde çok dilli 7/24 destek' },
    sv: { label: 'BPO Kontaktcenter', description: 'Flerspråkig 24/7-support på 12+ språk' },
    da: { label: 'BPO Kontaktcenter', description: 'Flersproget 24/7-support på 12+ sprog' },
  },
  '/services/digital-studio': {
    en: { label: 'Digital Factory & Dev Studio', description: 'Custom web, mobile, SaaS & AI development' },
    fr: { label: 'Studio Digital & Dev Factory', description: 'Développement web, mobile, SaaS & IA sur mesure' },
    es: { label: 'Fábrica Digital & Estudio Dev', description: 'Desarrollo web, móvil, SaaS e IA a medida' },
    ar: { label: 'المصنع الرقمي واستوديو التطوير', description: 'تطوير ويب وموبايل وSaaS وذكاء اصطناعي مخصص' },
    de: { label: 'Digitale Fabrik & Dev Studio', description: 'Maßgeschneiderte Web-, Mobile-, SaaS- & KI-Entwicklung' },
    it: { label: 'Fabbrica Digitale & Studio Dev', description: 'Sviluppo web, mobile, SaaS e IA su misura' },
    pt: { label: 'Fábrica Digital & Estúdio Dev', description: 'Desenvolvimento web, mobile, SaaS e IA sob medida' },
    nl: { label: 'Digitale Fabriek & Dev Studio', description: 'Op maat gemaakte web-, mobiel-, SaaS- en AI-ontwikkeling' },
    pl: { label: 'Fabryka Cyfrowa & Studio Dev', description: 'Rozwój web, mobile, SaaS i AI na zamówienie' },
    ru: { label: 'Цифровая Фабрика & Студия', description: 'Разработка веб, мобильных, SaaS и ИИ на заказ' },
    zh: { label: '数字工厂与开发工作室', description: '定制Web、移动、SaaS和AI开发' },
    ja: { label: 'デジタルファクトリー & 開発スタジオ', description: 'カスタムWeb・モバイル・SaaS・AI開発' },
    ko: { label: '디지털 팩토리 & 개발 스튜디오', description: '맞춤 웹, 모바일, SaaS & AI 개발' },
    tr: { label: 'Dijital Fabrika & Dev Stüdyo', description: 'Özel web, mobil, SaaS ve AI geliştirme' },
    sv: { label: 'Digital Fabrik & Dev Studio', description: 'Skräddarsydd webb-, mobil-, SaaS- och AI-utveckling' },
    da: { label: 'Digital Fabrik & Dev Studio', description: 'Skræddersyet web-, mobil-, SaaS- og AI-udvikling' },
  },
  '/why-morocco': {
    en: { label: 'Why Morocco', description: 'Nearshore advantage: GMT+1, 50-70% cost savings' },
    fr: { label: 'Pourquoi le Maroc', description: 'Avantage nearshore : GMT+1, 50-70% d\'économies' },
    es: { label: 'Por qué Marruecos', description: 'Ventaja nearshore: GMT+1, 50-70% de ahorro' },
    ar: { label: 'لماذا المغرب', description: 'ميزة النيرشور: GMT+1، توفير 50-70%' },
    de: { label: 'Warum Marokko', description: 'Nearshore-Vorteil: GMT+1, 50-70% Einsparung' },
    it: { label: 'Perché il Marocco', description: 'Vantaggio nearshore: GMT+1, 50-70% di risparmio' },
    pt: { label: 'Por que Marrocos', description: 'Vantagem nearshore: GMT+1, 50-70% de economia' },
    nl: { label: 'Waarom Marokko', description: 'Nearshore-voordeel: GMT+1, 50-70% besparing' },
    pl: { label: 'Dlaczego Maroko', description: 'Przewaga nearshore: GMT+1, 50-70% oszczędności' },
    ru: { label: 'Почему Марокко', description: 'Ниаршор: GMT+1, 50-70% экономии' },
    zh: { label: '为什么选择摩洛哥', description: '近岸优势：GMT+1，节省50-70%' },
    ja: { label: 'なぜモロッコか', description: 'ニアショアの優位性：GMT+1、50-70%のコスト削減' },
    ko: { label: '왜 모로코인가', description: '니어쇼어 이점: GMT+1, 50-70% 절감' },
    tr: { label: 'Neden Fas', description: 'Nearshore avantajı: GMT+1, %50-70 tasarruf' },
    sv: { label: 'Varför Marocko', description: 'Nearshore-fördel: GMT+1, 50-70% besparing' },
    da: { label: 'Hvorfor Marokko', description: 'Nearshore-fordel: GMT+1, 50-70% besparelse' },
  },
  '/pricing': {
    en: { label: 'Transparent Pricing', description: 'Per-hour, per-ticket & dedicated team models' },
    fr: { label: 'Tarification Transparente', description: 'Tarifs à l\'heure, au ticket et équipe dédiée' },
    es: { label: 'Precios Transparentes', description: 'Modelos por hora, por ticket y equipo dedicado' },
    ar: { label: 'تسعير شفاف', description: 'نماذج بالساعة أو التذكرة أو فريق مخصص' },
    de: { label: 'Transparente Preise', description: 'Pro Stunde, pro Ticket & dediziertes Team' },
    it: { label: 'Prezzi Trasparenti', description: 'Per ora, per ticket e team dedicato' },
    pt: { label: 'Preços Transparentes', description: 'Por hora, por ticket e equipe dedicada' },
    nl: { label: 'Transparante Prijzen', description: 'Per uur, per ticket en dedicated team' },
    pl: { label: 'Przejrzyste Ceny', description: 'Za godzinę, za zgłoszenie i dedykowany zespół' },
    ru: { label: 'Прозрачные Цены', description: 'За час, за тикет и выделенная команда' },
    zh: { label: '透明定价', description: '按小时、按工单和专属团队模式' },
    ja: { label: '透明な料金', description: '時間単位・チケット単位・専任チームモデル' },
    ko: { label: '투명한 요금제', description: '시간당, 티켓당, 전담팀 모델' },
    tr: { label: 'Şeffaf Fiyatlandırma', description: 'Saatlik, bilet başına ve özel ekip modelleri' },
    sv: { label: 'Transparenta Priser', description: 'Per timme, per ärende och dedikerat team' },
    da: { label: 'Transparente Priser', description: 'Pr. time, pr. billet og dedikeret team' },
  },
  '/case-studies': {
    en: { label: 'Case Studies', description: 'Real results from 50+ enterprise clients' },
    fr: { label: 'Études de Cas', description: 'Résultats réels de 50+ clients entreprise' },
    es: { label: 'Casos de Éxito', description: 'Resultados reales de 50+ clientes empresariales' },
    ar: { label: 'دراسات الحالة', description: 'نتائج حقيقية من 50+ عميل مؤسسي' },
    de: { label: 'Fallstudien', description: 'Echte Ergebnisse von 50+ Unternehmenskunden' },
    it: { label: 'Casi Studio', description: 'Risultati reali da 50+ clienti enterprise' },
    pt: { label: 'Estudos de Caso', description: 'Resultados reais de 50+ clientes empresariais' },
    nl: { label: 'Casestudies', description: 'Echte resultaten van 50+ bedrijfsklanten' },
    pl: { label: 'Studia Przypadków', description: 'Realne wyniki od 50+ klientów korporacyjnych' },
    ru: { label: 'Кейсы', description: 'Реальные результаты от 50+ корпоративных клиентов' },
    zh: { label: '案例研究', description: '50+企业客户的真实成果' },
    ja: { label: '事例紹介', description: '50+のエンタープライズ顧客の実績' },
    ko: { label: '사례 연구', description: '50+ 기업 고객의 실제 성과' },
    tr: { label: 'Vaka Çalışmaları', description: '50+ kurumsal müşteriden gerçek sonuçlar' },
    sv: { label: 'Fallstudier', description: 'Verkliga resultat från 50+ företagskunder' },
    da: { label: 'Casestudier', description: 'Rigtige resultater fra 50+ virksomhedskunder' },
  },
  '/contact': {
    en: { label: 'Get a Free Quote', description: 'Free assessment within 24 hours' },
    fr: { label: 'Devis Gratuit', description: 'Évaluation gratuite sous 24 heures' },
    es: { label: 'Presupuesto Gratis', description: 'Evaluación gratuita en 24 horas' },
    ar: { label: 'عرض سعر مجاني', description: 'تقييم مجاني خلال 24 ساعة' },
    de: { label: 'Kostenloses Angebot', description: 'Kostenlose Bewertung innerhalb von 24 Stunden' },
    it: { label: 'Preventivo Gratuito', description: 'Valutazione gratuita entro 24 ore' },
    pt: { label: 'Orçamento Grátis', description: 'Avaliação gratuita em 24 horas' },
    nl: { label: 'Gratis Offerte', description: 'Gratis beoordeling binnen 24 uur' },
    pl: { label: 'Darmowa Wycena', description: 'Bezpłatna ocena w ciągu 24 godzin' },
    ru: { label: 'Бесплатная Оценка', description: 'Бесплатная оценка в течение 24 часов' },
    zh: { label: '免费报价', description: '24小时内免费评估' },
    ja: { label: '無料見積もり', description: '24時間以内の無料アセスメント' },
    ko: { label: '무료 견적', description: '24시간 내 무료 평가' },
    tr: { label: 'Ücretsiz Teklif', description: '24 saat içinde ücretsiz değerlendirme' },
    sv: { label: 'Gratis Offert', description: 'Gratis utvärdering inom 24 timmar' },
    da: { label: 'Gratis Tilbud', description: 'Gratis vurdering inden for 24 timer' },
  },
  '/roi-calculator': {
    en: { label: 'ROI Calculator', description: 'Calculate your outsourcing savings in 2 minutes' },
    fr: { label: 'Calculateur ROI', description: 'Calculez vos économies d\'externalisation en 2 minutes' },
    es: { label: 'Calculadora ROI', description: 'Calcule sus ahorros de externalización en 2 minutos' },
    ar: { label: 'حاسبة العائد على الاستثمار', description: 'احسب وفوراتك من التعهيد في دقيقتين' },
    de: { label: 'ROI-Rechner', description: 'Berechnen Sie Ihre Outsourcing-Einsparungen in 2 Minuten' },
    it: { label: 'Calcolatore ROI', description: 'Calcola i risparmi dell\'outsourcing in 2 minuti' },
    pt: { label: 'Calculadora ROI', description: 'Calcule suas economias de terceirização em 2 minutos' },
    nl: { label: 'ROI-Calculator', description: 'Bereken uw outsourcing-besparingen in 2 minuten' },
    pl: { label: 'Kalkulator ROI', description: 'Oblicz oszczędności z outsourcingu w 2 minuty' },
    ru: { label: 'Калькулятор ROI', description: 'Рассчитайте экономию от аутсорсинга за 2 минуты' },
    zh: { label: 'ROI计算器', description: '2分钟内计算外包节省' },
    ja: { label: 'ROI計算機', description: '2分でアウトソーシングの節約額を計算' },
    ko: { label: 'ROI 계산기', description: '2분 만에 아웃소싱 절감액 계산' },
    tr: { label: 'ROI Hesaplayıcı', description: 'Dış kaynak tasarrufunuzu 2 dakikada hesaplayın' },
    sv: { label: 'ROI-Kalkylator', description: 'Beräkna dina outsourcing-besparingar på 2 minuter' },
    da: { label: 'ROI-Beregner', description: 'Beregn dine outsourcing-besparelser på 2 minutter' },
  },
  '/sla-guarantees': {
    en: { label: 'SLA Guarantees', description: 'Performance commitments: 99.9% uptime, <20s response' },
    fr: { label: 'Garanties SLA', description: 'Engagements de performance : 99,9% uptime, <20s réponse' },
    es: { label: 'Garantías SLA', description: 'Compromisos: 99,9% uptime, <20s respuesta' },
    ar: { label: 'ضمانات SLA', description: 'التزامات الأداء: 99.9% وقت تشغيل، <20 ثانية استجابة' },
    de: { label: 'SLA-Garantien', description: 'Leistungszusagen: 99,9% Verfügbarkeit, <20s Antwort' },
    it: { label: 'Garanzie SLA', description: 'Impegni: 99,9% uptime, <20s risposta' },
    pt: { label: 'Garantias SLA', description: 'Compromissos: 99,9% uptime, <20s resposta' },
    nl: { label: 'SLA-Garanties', description: 'Prestatie: 99,9% uptime, <20s reactietijd' },
    pl: { label: 'Gwarancje SLA', description: 'Zobowiązania: 99,9% uptime, <20s odpowiedź' },
    ru: { label: 'Гарантии SLA', description: 'Обязательства: 99,9% аптайм, <20с ответ' },
    zh: { label: 'SLA保证', description: '性能承诺：99.9%正常运行，<20秒响应' },
    ja: { label: 'SLA保証', description: 'パフォーマンス保証：99.9%稼働、<20秒応答' },
    ko: { label: 'SLA 보증', description: '성능 약속: 99.9% 가동률, <20초 응답' },
    tr: { label: 'SLA Garantileri', description: 'Performans taahhüdü: %99,9 çalışma, <20sn yanıt' },
    sv: { label: 'SLA-Garantier', description: 'Prestanda: 99,9% drifttid, <20s svar' },
    da: { label: 'SLA-Garantier', description: 'Ydeevne: 99,9% oppetid, <20s svar' },
  },
  '/security-compliance': {
    en: { label: 'Security & Compliance', description: 'GDPR, ISO 27001, PCI DSS compliance' },
    fr: { label: 'Sécurité & Conformité', description: 'Conformité RGPD, ISO 27001, PCI DSS' },
    es: { label: 'Seguridad y Cumplimiento', description: 'Cumplimiento GDPR, ISO 27001, PCI DSS' },
    ar: { label: 'الأمان والامتثال', description: 'امتثال GDPR وISO 27001 وPCI DSS' },
    de: { label: 'Sicherheit & Compliance', description: 'DSGVO, ISO 27001, PCI DSS Konformität' },
    it: { label: 'Sicurezza & Compliance', description: 'Conformità GDPR, ISO 27001, PCI DSS' },
    pt: { label: 'Segurança & Conformidade', description: 'Conformidade GDPR, ISO 27001, PCI DSS' },
    nl: { label: 'Beveiliging & Compliance', description: 'AVG, ISO 27001, PCI DSS naleving' },
    pl: { label: 'Bezpieczeństwo i Zgodność', description: 'Zgodność RODO, ISO 27001, PCI DSS' },
    ru: { label: 'Безопасность и Соответствие', description: 'Соответствие GDPR, ISO 27001, PCI DSS' },
    zh: { label: '安全与合规', description: 'GDPR、ISO 27001、PCI DSS合规' },
    ja: { label: 'セキュリティ & コンプライアンス', description: 'GDPR、ISO 27001、PCI DSS準拠' },
    ko: { label: '보안 및 규정 준수', description: 'GDPR, ISO 27001, PCI DSS 준수' },
    tr: { label: 'Güvenlik & Uyumluluk', description: 'GDPR, ISO 27001, PCI DSS uyumluluğu' },
    sv: { label: 'Säkerhet & Efterlevnad', description: 'GDPR, ISO 27001, PCI DSS efterlevnad' },
    da: { label: 'Sikkerhed & Compliance', description: 'GDPR, ISO 27001, PCI DSS overholdelse' },
  },
  '/blog': {
    en: { label: 'Industry Insights', description: 'Expert articles on BPO & tech trends' },
    fr: { label: 'Perspectives Industrie', description: 'Articles d\'experts sur les tendances BPO & tech' },
    es: { label: 'Perspectivas del Sector', description: 'Artículos expertos sobre tendencias BPO y tech' },
    ar: { label: 'رؤى الصناعة', description: 'مقالات خبراء حول اتجاهات BPO والتقنية' },
    de: { label: 'Branchen-Insights', description: 'Expertenartikel zu BPO- & Tech-Trends' },
    it: { label: 'Approfondimenti di Settore', description: 'Articoli specialistici su trend BPO e tech' },
    pt: { label: 'Insights do Setor', description: 'Artigos sobre tendências de BPO e tecnologia' },
    nl: { label: 'Branche-Inzichten', description: 'Expertartikelen over BPO- & tech-trends' },
    pl: { label: 'Branżowe Analizy', description: 'Artykuły eksperckie o trendach BPO i tech' },
    ru: { label: 'Аналитика Отрасли', description: 'Экспертные статьи о трендах BPO и технологий' },
    zh: { label: '行业洞察', description: '关于BPO和技术趋势的专家文章' },
    ja: { label: '業界インサイト', description: 'BPO＆テック動向の専門記事' },
    ko: { label: '업계 인사이트', description: 'BPO & 기술 트렌드 전문 기사' },
    tr: { label: 'Sektör Görüşleri', description: 'BPO ve teknoloji trendleri hakkında uzman makaleleri' },
    sv: { label: 'Branschinsikter', description: 'Expertartiklar om BPO- och tech-trender' },
    da: { label: 'Brancheindsigter', description: 'Ekspertartikler om BPO- og tech-trends' },
  },
  '/about': {
    en: { label: 'About Call IT Dev', description: 'Our mission, values and global presence' },
    fr: { label: 'À Propos', description: 'Notre mission, nos valeurs et notre présence mondiale' },
    es: { label: 'Sobre Nosotros', description: 'Nuestra misión, valores y presencia global' },
    ar: { label: 'من نحن', description: 'مهمتنا وقيمنا وحضورنا العالمي' },
    de: { label: 'Über Uns', description: 'Unsere Mission, Werte und globale Präsenz' },
    it: { label: 'Chi Siamo', description: 'La nostra missione, valori e presenza globale' },
    pt: { label: 'Sobre Nós', description: 'Nossa missão, valores e presença global' },
    nl: { label: 'Over Ons', description: 'Onze missie, waarden en wereldwijde aanwezigheid' },
    pl: { label: 'O Nas', description: 'Nasza misja, wartości i globalna obecność' },
    ru: { label: 'О Нас', description: 'Наша миссия, ценности и глобальное присутствие' },
    zh: { label: '关于我们', description: '我们的使命、价值观和全球布局' },
    ja: { label: '会社概要', description: '私たちの使命、価値観、グローバル展開' },
    ko: { label: '회사 소개', description: '미션, 가치 및 글로벌 프레즌스' },
    tr: { label: 'Hakkımızda', description: 'Misyonumuz, değerlerimiz ve küresel varlığımız' },
    sv: { label: 'Om Oss', description: 'Vår mission, värderingar och globala närvaro' },
    da: { label: 'Om Os', description: 'Vores mission, værdier og globale tilstedeværelse' },
  },
  '/leadership': {
    en: { label: 'Leadership Team', description: 'Meet the experts behind Call IT Dev' },
    fr: { label: 'Équipe de Direction', description: 'Découvrez les experts derrière Call IT Dev' },
    es: { label: 'Equipo Directivo', description: 'Conozca a los expertos de Call IT Dev' },
    ar: { label: 'فريق القيادة', description: 'تعرّف على الخبراء وراء Call IT Dev' },
    de: { label: 'Führungsteam', description: 'Lernen Sie die Experten hinter Call IT Dev kennen' },
    it: { label: 'Team di Leadership', description: 'Scopri gli esperti di Call IT Dev' },
    pt: { label: 'Equipe de Liderança', description: 'Conheça os especialistas da Call IT Dev' },
    nl: { label: 'Leiderschapsteam', description: 'Ontmoet de experts achter Call IT Dev' },
    pl: { label: 'Zespół Kierowniczy', description: 'Poznaj ekspertów Call IT Dev' },
    ru: { label: 'Команда Руководства', description: 'Познакомьтесь с экспертами Call IT Dev' },
    zh: { label: '领导团队', description: '认识Call IT Dev背后的专家' },
    ja: { label: 'リーダーシップチーム', description: 'Call IT Devの専門家をご紹介' },
    ko: { label: '리더십 팀', description: 'Call IT Dev 전문가를 만나보세요' },
    tr: { label: 'Yönetim Ekibi', description: 'Call IT Dev arkasındaki uzmanlarla tanışın' },
    sv: { label: 'Ledningsgrupp', description: 'Möt experterna bakom Call IT Dev' },
    da: { label: 'Ledelsesteam', description: 'Mød eksperterne bag Call IT Dev' },
  },
  '/trust-center': {
    en: { label: 'Trust Center', description: 'Enterprise security documentation hub' },
    fr: { label: 'Centre de Confiance', description: 'Hub de documentation sécurité entreprise' },
    es: { label: 'Centro de Confianza', description: 'Hub de documentación de seguridad empresarial' },
    ar: { label: 'مركز الثقة', description: 'مركز وثائق الأمان المؤسسي' },
    de: { label: 'Trust Center', description: 'Sicherheitsdokumentation für Unternehmen' },
    it: { label: 'Centro Fiducia', description: 'Hub documentazione sicurezza aziendale' },
    pt: { label: 'Central de Confiança', description: 'Hub de documentação de segurança empresarial' },
    nl: { label: 'Vertrouwenscentrum', description: 'Hub voor beveiligingsdocumentatie' },
    pl: { label: 'Centrum Zaufania', description: 'Hub dokumentacji bezpieczeństwa' },
    ru: { label: 'Центр Доверия', description: 'Хаб документации по безопасности' },
    zh: { label: '信任中心', description: '企业安全文档中心' },
    ja: { label: 'トラストセンター', description: 'エンタープライズセキュリティドキュメント' },
    ko: { label: '신뢰 센터', description: '기업 보안 문서 허브' },
    tr: { label: 'Güven Merkezi', description: 'Kurumsal güvenlik dokümantasyon merkezi' },
    sv: { label: 'Förtroendecenter', description: 'Säkerhetsdokumentation för företag' },
    da: { label: 'Tillidscenter', description: 'Sikkerhedsdokumentation til virksomheder' },
  },
  '/solutions/digital-ops': {
    en: { label: 'Digital Operations', description: 'BPO 4.0 transformation & automation' },
    fr: { label: 'Opérations Digitales', description: 'Transformation BPO 4.0 & automatisation' },
    es: { label: 'Operaciones Digitales', description: 'Transformación BPO 4.0 y automatización' },
    ar: { label: 'العمليات الرقمية', description: 'تحول BPO 4.0 والأتمتة' },
    de: { label: 'Digitale Operationen', description: 'BPO 4.0 Transformation & Automatisierung' },
    it: { label: 'Operazioni Digitali', description: 'Trasformazione BPO 4.0 e automazione' },
    pt: { label: 'Operações Digitais', description: 'Transformação BPO 4.0 e automação' },
    nl: { label: 'Digitale Operaties', description: 'BPO 4.0 transformatie en automatisering' },
    pl: { label: 'Operacje Cyfrowe', description: 'Transformacja BPO 4.0 i automatyzacja' },
    ru: { label: 'Цифровые Операции', description: 'Трансформация BPO 4.0 и автоматизация' },
    zh: { label: '数字运营', description: 'BPO 4.0转型与自动化' },
    ja: { label: 'デジタルオペレーション', description: 'BPO 4.0変革とオートメーション' },
    ko: { label: '디지털 운영', description: 'BPO 4.0 혁신 & 자동화' },
    tr: { label: 'Dijital Operasyonlar', description: 'BPO 4.0 dönüşüm ve otomasyon' },
    sv: { label: 'Digitala Operationer', description: 'BPO 4.0 transformation och automatisering' },
    da: { label: 'Digitale Operationer', description: 'BPO 4.0 transformation og automatisering' },
  },
  '/solutions/bpo-contact-center': {
    en: { label: 'BPO Contact Center', description: '24/7 multilingual omnichannel support' },
    fr: { label: 'Centre de Contact BPO', description: 'Support omnicanal multilingue 24/7' },
    es: { label: 'Centro de Contacto BPO', description: 'Soporte omnicanal multilingüe 24/7' },
    ar: { label: 'مركز اتصال BPO', description: 'دعم متعدد القنوات واللغات 24/7' },
    de: { label: 'BPO Contact Center', description: '24/7 mehrsprachiger Omnichannel-Support' },
    it: { label: 'Contact Center BPO', description: 'Supporto omnicanale multilingue 24/7' },
    pt: { label: 'Contact Center BPO', description: 'Suporte omnicanal multilíngue 24/7' },
    nl: { label: 'BPO Contactcenter', description: '24/7 meertalig omnichannel-ondersteuning' },
    pl: { label: 'Centrum Kontaktowe BPO', description: 'Wielojęzyczne wsparcie omnichannel 24/7' },
    ru: { label: 'Контакт-центр BPO', description: 'Многоязычная омниканальная поддержка 24/7' },
    zh: { label: 'BPO联络中心', description: '24/7多语言全渠道支持' },
    ja: { label: 'BPOコンタクトセンター', description: '24時間多言語オムニチャネルサポート' },
    ko: { label: 'BPO 컨택센터', description: '24/7 다국어 옴니채널 지원' },
    tr: { label: 'BPO İletişim Merkezi', description: '7/24 çok dilli omnichannel destek' },
    sv: { label: 'BPO Kontaktcenter', description: '24/7 flerspråkig omnikanalssupport' },
    da: { label: 'BPO Kontaktcenter', description: '24/7 flersproget omnichannel-support' },
  },
  '/services/web-development': {
    en: { label: 'Web Development', description: 'React, cloud-native enterprise portals' },
    fr: { label: 'Développement Web', description: 'Portails entreprise React & cloud-native' },
    es: { label: 'Desarrollo Web', description: 'Portales empresariales React y cloud-native' },
    ar: { label: 'تطوير الويب', description: 'بوابات مؤسسية React وسحابية' },
    de: { label: 'Webentwicklung', description: 'React, Cloud-native Unternehmensportale' },
    it: { label: 'Sviluppo Web', description: 'Portali aziendali React e cloud-native' },
    pt: { label: 'Desenvolvimento Web', description: 'Portais empresariais React e cloud-native' },
    nl: { label: 'Webontwikkeling', description: 'React, cloud-native bedrijfsportalen' },
    pl: { label: 'Rozwój Webowy', description: 'Portale korporacyjne React i cloud-native' },
    ru: { label: 'Веб-Разработка', description: 'React, облачные корпоративные порталы' },
    zh: { label: 'Web开发', description: 'React、云原生企业门户' },
    ja: { label: 'Web開発', description: 'React、クラウドネイティブ企業ポータル' },
    ko: { label: '웹 개발', description: 'React, 클라우드 네이티브 엔터프라이즈 포털' },
    tr: { label: 'Web Geliştirme', description: 'React, bulut tabanlı kurumsal portallar' },
    sv: { label: 'Webbutveckling', description: 'React, molnbaserade företagsportaler' },
    da: { label: 'Webudvikling', description: 'React, cloud-native virksomhedsportaler' },
  },
  '/services/ai-solutions': {
    en: { label: 'AI Solutions', description: 'Intelligent automation & AI integration' },
    fr: { label: 'Solutions IA', description: 'Automatisation intelligente & intégration IA' },
    es: { label: 'Soluciones IA', description: 'Automatización inteligente e integración de IA' },
    ar: { label: 'حلول الذكاء الاصطناعي', description: 'أتمتة ذكية وتكامل الذكاء الاصطناعي' },
    de: { label: 'KI-Lösungen', description: 'Intelligente Automatisierung & KI-Integration' },
    it: { label: 'Soluzioni IA', description: 'Automazione intelligente e integrazione IA' },
    pt: { label: 'Soluções IA', description: 'Automação inteligente e integração de IA' },
    nl: { label: 'AI-Oplossingen', description: 'Intelligente automatisering en AI-integratie' },
    pl: { label: 'Rozwiązania AI', description: 'Inteligentna automatyzacja i integracja AI' },
    ru: { label: 'Решения ИИ', description: 'Интеллектуальная автоматизация и интеграция ИИ' },
    zh: { label: 'AI解决方案', description: '智能自动化与AI集成' },
    ja: { label: 'AIソリューション', description: 'インテリジェント自動化とAI統合' },
    ko: { label: 'AI 솔루션', description: '지능형 자동화 & AI 통합' },
    tr: { label: 'AI Çözümleri', description: 'Akıllı otomasyon ve AI entegrasyonu' },
    sv: { label: 'AI-Lösningar', description: 'Intelligent automatisering och AI-integration' },
    da: { label: 'AI-Løsninger', description: 'Intelligent automatisering og AI-integration' },
  },
  '/booking': {
    en: { label: 'Schedule a Demo', description: 'Book a free consultation with our experts' },
    fr: { label: 'Réserver une Démo', description: 'Réservez une consultation gratuite' },
    es: { label: 'Agendar Demo', description: 'Reserve una consulta gratuita' },
    ar: { label: 'حجز عرض توضيحي', description: 'احجز استشارة مجانية مع خبرائنا' },
    de: { label: 'Demo Buchen', description: 'Kostenlose Beratung buchen' },
    it: { label: 'Prenota una Demo', description: 'Prenota una consulenza gratuita' },
    pt: { label: 'Agendar Demo', description: 'Agende uma consultoria gratuita' },
    nl: { label: 'Demo Boeken', description: 'Boek een gratis adviesgesprek' },
    pl: { label: 'Zarezerwuj Demo', description: 'Zarezerwuj bezpłatną konsultację' },
    ru: { label: 'Записаться на Демо', description: 'Запишитесь на бесплатную консультацию' },
    zh: { label: '预约演示', description: '预约免费专家咨询' },
    ja: { label: 'デモ予約', description: '無料コンサルテーション予約' },
    ko: { label: '데모 예약', description: '무료 전문가 상담 예약' },
    tr: { label: 'Demo Rezervasyonu', description: 'Uzmanlarımızla ücretsiz danışma ayırtın' },
    sv: { label: 'Boka Demo', description: 'Boka en gratis konsultation' },
    da: { label: 'Book Demo', description: 'Book en gratis konsultation' },
  },
  '/trust/trust-model': {
    en: { label: 'Trust Model', description: 'Our comprehensive trust framework' },
    fr: { label: 'Modèle de Confiance', description: 'Notre cadre de confiance complet' },
    es: { label: 'Modelo de Confianza', description: 'Nuestro marco integral de confianza' },
    ar: { label: 'نموذج الثقة', description: 'إطار الثقة الشامل لدينا' },
    de: { label: 'Vertrauensmodell', description: 'Unser umfassendes Vertrauens-Framework' },
    it: { label: 'Modello di Fiducia', description: 'Il nostro framework di fiducia completo' },
    pt: { label: 'Modelo de Confiança', description: 'Nosso framework abrangente de confiança' },
    nl: { label: 'Vertrouwensmodel', description: 'Ons uitgebreide vertrouwenskader' },
    pl: { label: 'Model Zaufania', description: 'Nasz kompleksowy framework zaufania' },
    ru: { label: 'Модель Доверия', description: 'Наш комплексный фреймворк доверия' },
    zh: { label: '信任模型', description: '我们的全面信任框架' },
    ja: { label: 'トラストモデル', description: '包括的な信頼フレームワーク' },
    ko: { label: '신뢰 모델', description: '포괄적인 신뢰 프레임워크' },
    tr: { label: 'Güven Modeli', description: 'Kapsamlı güven çerçevemiz' },
    sv: { label: 'Förtroendemodell', description: 'Vårt omfattande förtroendeframverk' },
    da: { label: 'Tillidsmodel', description: 'Vores omfattende tillidsrammeværk' },
  },
  '/trust/security-assurance': {
    en: { label: 'Security Assurance', description: 'Continuous testing & verification' },
    fr: { label: 'Assurance Sécurité', description: 'Tests et vérifications continus' },
    es: { label: 'Aseguramiento de Seguridad', description: 'Pruebas y verificación continuas' },
    ar: { label: 'ضمان الأمان', description: 'اختبار وتحقق مستمر' },
    de: { label: 'Sicherheitsgarantie', description: 'Kontinuierliche Tests & Verifizierung' },
    it: { label: 'Garanzia di Sicurezza', description: 'Test e verifica continui' },
    pt: { label: 'Garantia de Segurança', description: 'Testes e verificação contínuos' },
    nl: { label: 'Beveiligingsgarantie', description: 'Continue testen en verificatie' },
    pl: { label: 'Zapewnienie Bezpieczeństwa', description: 'Ciągłe testy i weryfikacja' },
    ru: { label: 'Обеспечение Безопасности', description: 'Непрерывное тестирование и верификация' },
    zh: { label: '安全保障', description: '持续测试与验证' },
    ja: { label: 'セキュリティ保証', description: '継続的テストと検証' },
    ko: { label: '보안 보증', description: '지속적 테스트 및 검증' },
    tr: { label: 'Güvenlik Güvencesi', description: 'Sürekli test ve doğrulama' },
    sv: { label: 'Säkerhetsgaranti', description: 'Kontinuerlig testning och verifiering' },
    da: { label: 'Sikkerhedsgaranti', description: 'Kontinuerlig test og verifikation' },
  },
  '/trust/security-culture': {
    en: { label: 'Security Culture', description: 'Training & awareness programs' },
    fr: { label: 'Culture Sécurité', description: 'Programmes de formation et sensibilisation' },
    es: { label: 'Cultura de Seguridad', description: 'Programas de formación y concienciación' },
    ar: { label: 'ثقافة الأمان', description: 'برامج التدريب والتوعية' },
    de: { label: 'Sicherheitskultur', description: 'Schulungs- & Sensibilisierungsprogramme' },
    it: { label: 'Cultura della Sicurezza', description: 'Programmi di formazione e sensibilizzazione' },
    pt: { label: 'Cultura de Segurança', description: 'Programas de treinamento e conscientização' },
    nl: { label: 'Veiligheidscultuur', description: 'Training- en bewustwordingsprogramma\'s' },
    pl: { label: 'Kultura Bezpieczeństwa', description: 'Programy szkoleniowe i uświadamiające' },
    ru: { label: 'Культура Безопасности', description: 'Программы обучения и осведомлённости' },
    zh: { label: '安全文化', description: '培训与意识计划' },
    ja: { label: 'セキュリティ文化', description: 'トレーニング＆啓発プログラム' },
    ko: { label: '보안 문화', description: '교육 및 인식 프로그램' },
    tr: { label: 'Güvenlik Kültürü', description: 'Eğitim ve farkındalık programları' },
    sv: { label: 'Säkerhetskultur', description: 'Utbildnings- och medvetenhetsprogram' },
    da: { label: 'Sikkerhedskultur', description: 'Uddannelses- og bevidsthedsprogrammer' },
  },
  '/trust/ai-governance': {
    en: { label: 'AI Governance', description: 'Responsible AI policies & oversight' },
    fr: { label: 'Gouvernance IA', description: 'Politiques et supervision IA responsable' },
    es: { label: 'Gobernanza IA', description: 'Políticas de IA responsable y supervisión' },
    ar: { label: 'حوكمة الذكاء الاصطناعي', description: 'سياسات وإشراف الذكاء الاصطناعي المسؤول' },
    de: { label: 'KI-Governance', description: 'Verantwortungsvolle KI-Richtlinien & Aufsicht' },
    it: { label: 'Governance IA', description: 'Politiche e supervisione IA responsabile' },
    pt: { label: 'Governança IA', description: 'Políticas de IA responsável e supervisão' },
    nl: { label: 'AI-Governance', description: 'Verantwoord AI-beleid en toezicht' },
    pl: { label: 'Zarządzanie AI', description: 'Polityki odpowiedzialnego AI i nadzór' },
    ru: { label: 'Управление ИИ', description: 'Политика ответственного ИИ и надзор' },
    zh: { label: 'AI治理', description: '负责任的AI政策与监督' },
    ja: { label: 'AIガバナンス', description: '責任あるAIポリシーと監視' },
    ko: { label: 'AI 거버넌스', description: '책임감 있는 AI 정책 및 감독' },
    tr: { label: 'AI Yönetişimi', description: 'Sorumlu AI politikaları ve denetim' },
    sv: { label: 'AI-Styrning', description: 'Ansvarsfull AI-policy och tillsyn' },
    da: { label: 'AI-Governance', description: 'Ansvarlig AI-politik og tilsyn' },
  },
  '/careers': {
    en: { label: 'Careers', description: 'Join our growing global team' },
    fr: { label: 'Carrières', description: 'Rejoignez notre équipe mondiale en croissance' },
    es: { label: 'Carreras', description: 'Únete a nuestro equipo global en crecimiento' },
    ar: { label: 'الوظائف', description: 'انضم إلى فريقنا العالمي المتنامي' },
    de: { label: 'Karriere', description: 'Werden Sie Teil unseres wachsenden Teams' },
    it: { label: 'Carriere', description: 'Unisciti al nostro team globale in crescita' },
    pt: { label: 'Carreiras', description: 'Junte-se à nossa equipe global em crescimento' },
    nl: { label: 'Carrières', description: 'Word lid van ons groeiende team' },
    pl: { label: 'Kariera', description: 'Dołącz do naszego rosnącego zespołu' },
    ru: { label: 'Карьера', description: 'Присоединяйтесь к нашей растущей команде' },
    zh: { label: '招聘', description: '加入我们不断壮大的全球团队' },
    ja: { label: '採用情報', description: '成長するグローバルチームに参加' },
    ko: { label: '채용', description: '성장하는 글로벌 팀에 합류하세요' },
    tr: { label: 'Kariyer', description: 'Büyüyen küresel ekibimize katılın' },
    sv: { label: 'Karriär', description: 'Gå med i vårt växande globala team' },
    da: { label: 'Karriere', description: 'Bliv en del af vores voksende globale team' },
  },
  '/locations/morocco': {
    en: { label: 'Morocco HQ', description: 'Main operations hub in Casablanca' },
    fr: { label: 'Siège Maroc', description: 'Hub opérationnel principal à Casablanca' },
    es: { label: 'Sede Marruecos', description: 'Centro operativo principal en Casablanca' },
    ar: { label: 'المقر المغرب', description: 'مركز العمليات الرئيسي في الدار البيضاء' },
    de: { label: 'Hauptsitz Marokko', description: 'Hauptstandort in Casablanca' },
    it: { label: 'Sede Marocco', description: 'Hub operativo principale a Casablanca' },
    pt: { label: 'Sede Marrocos', description: 'Hub operacional principal em Casablanca' },
    nl: { label: 'Hoofdkantoor Marokko', description: 'Operationele hub in Casablanca' },
    pl: { label: 'Siedziba Maroko', description: 'Główny hub operacyjny w Casablance' },
    ru: { label: 'Штаб-квартира Марокко', description: 'Основной операционный хаб в Касабланке' },
    zh: { label: '摩洛哥总部', description: '卡萨布兰卡主运营中心' },
    ja: { label: 'モロッコ本社', description: 'カサブランカのメインオペレーションハブ' },
    ko: { label: '모로코 본부', description: '카사블랑카 메인 운영 허브' },
    tr: { label: 'Fas Merkezi', description: 'Kazablanka\'daki ana operasyon merkezi' },
    sv: { label: 'Marocko HQ', description: 'Huvudkontor i Casablanca' },
    da: { label: 'Marokko HQ', description: 'Hovedkontor i Casablanca' },
  },
  '/locations/uae': {
    en: { label: 'Dubai Office', description: 'Middle East & GCC operations hub' },
    fr: { label: 'Bureau Dubaï', description: 'Hub opérationnel Moyen-Orient & GCC' },
    es: { label: 'Oficina Dubái', description: 'Hub de operaciones Oriente Medio y GCC' },
    ar: { label: 'مكتب دبي', description: 'مركز عمليات الشرق الأوسط ومجلس التعاون' },
    de: { label: 'Büro Dubai', description: 'Nahost- & GCC-Operationen' },
    it: { label: 'Ufficio Dubai', description: 'Hub operativo Medio Oriente e GCC' },
    pt: { label: 'Escritório Dubai', description: 'Hub Oriente Médio e GCC' },
    nl: { label: 'Kantoor Dubai', description: 'Midden-Oosten & GCC operaties' },
    pl: { label: 'Biuro Dubaj', description: 'Hub Bliski Wschód & GCC' },
    ru: { label: 'Офис Дубай', description: 'Хаб для Ближнего Востока и GCC' },
    zh: { label: '迪拜办事处', description: '中东与GCC运营中心' },
    ja: { label: 'ドバイオフィス', description: '中東・GCCオペレーションハブ' },
    ko: { label: '두바이 사무소', description: '중동 & GCC 운영 허브' },
    tr: { label: 'Dubai Ofisi', description: 'Ortadoğu ve KİK operasyon merkezi' },
    sv: { label: 'Dubai-kontor', description: 'Mellanöstern & GCC-operationer' },
    da: { label: 'Dubai-kontor', description: 'Mellemøsten & GCC-operationer' },
  },
  '/glossary': {
    en: { label: 'BPO Glossary', description: 'Industry terminology & definitions' },
    fr: { label: 'Glossaire BPO', description: 'Terminologie et définitions du secteur' },
    es: { label: 'Glosario BPO', description: 'Terminología y definiciones del sector' },
    ar: { label: 'مسرد BPO', description: 'مصطلحات وتعريفات الصناعة' },
    de: { label: 'BPO-Glossar', description: 'Branchenbegriffe & Definitionen' },
    it: { label: 'Glossario BPO', description: 'Terminologia e definizioni del settore' },
    pt: { label: 'Glossário BPO', description: 'Terminologia e definições do setor' },
    nl: { label: 'BPO Woordenlijst', description: 'Brancheterminologie en definities' },
    pl: { label: 'Słownik BPO', description: 'Terminologia i definicje branżowe' },
    ru: { label: 'Глоссарий BPO', description: 'Отраслевая терминология и определения' },
    zh: { label: 'BPO词汇表', description: '行业术语与定义' },
    ja: { label: 'BPO用語集', description: '業界用語と定義' },
    ko: { label: 'BPO 용어집', description: '업계 용어 및 정의' },
    tr: { label: 'BPO Sözlüğü', description: 'Sektör terminolojisi ve tanımları' },
    sv: { label: 'BPO-Ordlista', description: 'Branschterminologi och definitioner' },
    da: { label: 'BPO-Ordliste', description: 'Brancheterminologi og definitioner' },
  },
  '/call-center-for-france': {
    en: { label: 'BPO for France', description: 'Native French agents, CET timezone' },
    fr: { label: 'Centre d\'appel France', description: 'Agents natifs français, fuseau CET' },
    es: { label: 'BPO para Francia', description: 'Agentes franceses nativos, zona CET' },
    ar: { label: 'BPO لفرنسا', description: 'وكلاء فرنسيون أصليون' },
    de: { label: 'BPO für Frankreich', description: 'Muttersprachliche französische Agents' },
    it: { label: 'BPO per la Francia', description: 'Agenti madrelingua francese' },
    pt: { label: 'BPO para França', description: 'Agentes franceses nativos' },
    nl: { label: 'BPO voor Frankrijk', description: 'Moedertaal Franse agenten' },
    pl: { label: 'BPO dla Francji', description: 'Natywni agenci francuskojęzyczni' },
    ru: { label: 'BPO для Франции', description: 'Французские операторы' },
    zh: { label: '法国BPO', description: '母语法语座席' },
    ja: { label: 'フランス向けBPO', description: 'ネイティブフランス語エージェント' },
    ko: { label: '프랑스 BPO', description: '프랑스어 원어민 상담원' },
    tr: { label: 'Fransa için BPO', description: 'Anadil Fransızca ajanlar' },
    sv: { label: 'BPO för Frankrike', description: 'Franska modersmålsagenter' },
    da: { label: 'BPO for Frankrig', description: 'Franske modersmålsagenter' },
  },
  '/call-center-for-germany': {
    en: { label: 'BPO for Germany', description: 'German-speaking agents, GDPR-compliant' },
    fr: { label: 'Centre d\'appel Allemagne', description: 'Agents germanophones, conforme RGPD' },
    es: { label: 'BPO para Alemania', description: 'Agentes germanófonos, GDPR' },
    ar: { label: 'BPO لألمانيا', description: 'وكلاء ناطقون بالألمانية' },
    de: { label: 'BPO für Deutschland', description: 'Deutschsprachige Agents, DSGVO-konform' },
    it: { label: 'BPO per la Germania', description: 'Agenti germanofoni, GDPR' },
    pt: { label: 'BPO para Alemanha', description: 'Agentes germanófonos, GDPR' },
    nl: { label: 'BPO voor Duitsland', description: 'Duitstalige agenten, AVG-conform' },
    pl: { label: 'BPO dla Niemiec', description: 'Agenci niemieckojęzyczni, RODO' },
    ru: { label: 'BPO для Германии', description: 'Немецкоязычные операторы, GDPR' },
    zh: { label: '德国BPO', description: '德语座席，符合GDPR' },
    ja: { label: 'ドイツ向けBPO', description: 'ドイツ語エージェント、GDPR準拠' },
    ko: { label: '독일 BPO', description: '독일어 상담원, GDPR 준수' },
    tr: { label: 'Almanya için BPO', description: 'Almanca konuşan ajanlar, KVKK uyumlu' },
    sv: { label: 'BPO för Tyskland', description: 'Tysktalande agenter, GDPR' },
    da: { label: 'BPO for Tyskland', description: 'Tysktalende agenter, GDPR' },
  },
  '/call-center-for-usa': {
    en: { label: 'BPO for USA', description: 'Bilingual English/Spanish, 24/7, 70% savings' },
    fr: { label: 'BPO pour les USA', description: 'Bilingue anglais/espagnol, 24/7, 70% économies' },
    es: { label: 'BPO para EE.UU.', description: 'Bilingüe inglés/español, 24/7, 70% ahorro' },
    ar: { label: 'BPO للولايات المتحدة', description: 'ثنائي اللغة إنجليزي/إسباني' },
    de: { label: 'BPO für die USA', description: 'Zweisprachig Englisch/Spanisch, 24/7' },
    it: { label: 'BPO per gli USA', description: 'Bilingue inglese/spagnolo, 24/7' },
    pt: { label: 'BPO para os EUA', description: 'Bilíngue inglês/espanhol, 24/7' },
    nl: { label: 'BPO voor de VS', description: 'Tweetalig Engels/Spaans, 24/7' },
    pl: { label: 'BPO dla USA', description: 'Dwujęzyczni agenci angielski/hiszpański' },
    ru: { label: 'BPO для США', description: 'Двуязычный англ/испанский, 24/7' },
    zh: { label: '美国BPO', description: '英语/西班牙语双语，全天候' },
    ja: { label: 'アメリカ向けBPO', description: '英語/スペイン語バイリンガル' },
    ko: { label: '미국 BPO', description: '영어/스페인어 이중 언어, 24/7' },
    tr: { label: 'ABD için BPO', description: 'İki dilli İngilizce/İspanyolca, 7/24' },
    sv: { label: 'BPO för USA', description: 'Tvåspråkig engelska/spanska, 24/7' },
    da: { label: 'BPO for USA', description: 'Tosproget engelsk/spansk, 24/7' },
  },
  '/guides/gdpr-compliance-outsourcing': {
    en: { label: 'GDPR Compliance Guide', description: 'Complete guide to GDPR-compliant outsourcing' },
    fr: { label: 'Guide Conformité RGPD', description: 'Guide complet pour l\'externalisation conforme au RGPD' },
    es: { label: 'Guía Cumplimiento GDPR', description: 'Guía completa de externalización conforme al GDPR' },
    ar: { label: 'دليل الامتثال GDPR', description: 'دليل كامل للاستعانة بمصادر خارجية متوافقة مع GDPR' },
    de: { label: 'DSGVO-Compliance-Leitfaden', description: 'Vollständiger Leitfaden für DSGVO-konformes Outsourcing' },
    it: { label: 'Guida alla Conformità GDPR', description: 'Guida completa all\'outsourcing conforme al GDPR' },
    pt: { label: 'Guia de Conformidade GDPR', description: 'Guia completo para outsourcing em conformidade com o GDPR' },
    nl: { label: 'AVG-Compliance Gids', description: 'Complete gids voor AVG-conforme outsourcing' },
    pl: { label: 'Przewodnik Zgodności RODO', description: 'Kompletny przewodnik po outsourcingu zgodnym z RODO' },
    ru: { label: 'Руководство по GDPR', description: 'Полное руководство по GDPR-совместимому аутсорсингу' },
    zh: { label: 'GDPR 合规指南', description: 'GDPR 合规外包完整指南' },
    ja: { label: 'GDPR 準拠ガイド', description: 'GDPR準拠アウトソーシングの完全ガイド' },
    ko: { label: 'GDPR 준수 가이드', description: 'GDPR 준수 아웃소싱 완전 가이드' },
    tr: { label: 'GDPR Uyum Rehberi', description: 'GDPR uyumlu dış kaynak kullanımı için kapsamlı rehber' },
    sv: { label: 'GDPR-guide', description: 'Komplett guide för GDPR-kompatibel outsourcing' },
    da: { label: 'GDPR-Guide', description: 'Komplet guide til GDPR-kompatibel outsourcing' },
  },
  '/guides/nearshore-vs-offshore-outsourcing': {
    en: { label: 'Nearshore vs Offshore Guide', description: 'Data-driven comparison of outsourcing models' },
    fr: { label: 'Guide Nearshore vs Offshore', description: 'Comparaison basée sur les données des modèles d\'externalisation' },
    es: { label: 'Guía Nearshore vs Offshore', description: 'Comparación basada en datos de modelos de externalización' },
    ar: { label: 'دليل القريب مقابل البعيد', description: 'مقارنة قائمة على البيانات لنماذج الاستعانة بمصادر خارجية' },
    de: { label: 'Nearshore vs Offshore Leitfaden', description: 'Datengestützter Vergleich der Outsourcing-Modelle' },
    it: { label: 'Guida Nearshore vs Offshore', description: 'Confronto data-driven dei modelli di outsourcing' },
    pt: { label: 'Guia Nearshore vs Offshore', description: 'Comparação orientada por dados dos modelos de outsourcing' },
    nl: { label: 'Nearshore vs Offshore Gids', description: 'Datagedreven vergelijking van outsourcingmodellen' },
    pl: { label: 'Przewodnik Nearshore vs Offshore', description: 'Porównanie modeli outsourcingu oparte na danych' },
    ru: { label: 'Гид Nearshore vs Offshore', description: 'Сравнение моделей аутсорсинга на основе данных' },
    zh: { label: '近岸 vs 离岸指南', description: '基于数据的外包模式对比' },
    ja: { label: 'ニアショア vs オフショア ガイド', description: 'データに基づくアウトソーシングモデル比較' },
    ko: { label: '니어쇼어 vs 오프쇼어 가이드', description: '데이터 기반 아웃소싱 모델 비교' },
    tr: { label: 'Nearshore vs Offshore Rehberi', description: 'Dış kaynak modellerinin veriye dayalı karşılaştırması' },
    sv: { label: 'Nearshore vs Offshore-guide', description: 'Datadriven jämförelse av outsourcingmodeller' },
    da: { label: 'Nearshore vs Offshore-guide', description: 'Datadrevet sammenligning af outsourcingmodeller' },
  },
  '/guides/ai-call-center-automation-2026': {
    en: { label: 'AI Call Center Guide 2026', description: 'How AI transforms call center operations' },
    fr: { label: 'Guide IA Centre d\'Appel 2026', description: 'Comment l\'IA transforme les opérations de centre d\'appel' },
    es: { label: 'Guía IA Call Center 2026', description: 'Cómo la IA transforma los centros de llamadas' },
    ar: { label: 'دليل الذكاء الاصطناعي لمراكز الاتصال 2026', description: 'كيف يحول الذكاء الاصطناعي عمليات مراكز الاتصال' },
    de: { label: 'KI Call Center Leitfaden 2026', description: 'Wie KI Call-Center-Operationen transformiert' },
    it: { label: 'Guida AI Call Center 2026', description: 'Come l\'AI trasforma le operazioni di call center' },
    pt: { label: 'Guia de IA para Call Center 2026', description: 'Como a IA transforma operações de call center' },
    nl: { label: 'AI Callcenter Gids 2026', description: 'Hoe AI callcenter-operaties transformeert' },
    pl: { label: 'Przewodnik AI Contact Center 2026', description: 'Jak AI zmienia operacje contact center' },
    ru: { label: 'Гид по AI Call Center 2026', description: 'Как ИИ трансформирует операции колл-центра' },
    zh: { label: 'AI 呼叫中心指南 2026', description: 'AI 如何重塑呼叫中心运营' },
    ja: { label: 'AI コールセンターガイド 2026', description: 'AIがコールセンター運営をどう変えるか' },
    ko: { label: 'AI 콜센터 가이드 2026', description: 'AI가 콜센터 운영을 어떻게 혁신하는지' },
    tr: { label: 'AI Çağrı Merkezi Rehberi 2026', description: 'Yapay zekâ çağrı merkezi operasyonlarını nasıl dönüştürüyor' },
    sv: { label: 'AI Call Center-guide 2026', description: 'Hur AI förändrar callcenter-verksamhet' },
    da: { label: 'AI Call Center-guide 2026', description: 'Hvordan AI transformerer callcenter-drift' },
  },
  '/guides/outsourcing-roi-calculator': {
    en: { label: 'Outsourcing ROI Calculator', description: 'Interactive tool to calculate your BPO savings' },
    fr: { label: 'Calculateur ROI Externalisation', description: 'Outil interactif pour calculer vos économies BPO' },
    es: { label: 'Calculadora ROI Externalización', description: 'Herramienta interactiva para calcular sus ahorros BPO' },
    ar: { label: 'حاسبة العائد على الاستثمار', description: 'أداة تفاعلية لحساب مدخرات BPO' },
    de: { label: 'Outsourcing ROI-Rechner', description: 'Interaktives Tool zur Berechnung Ihrer BPO-Einsparungen' },
    it: { label: 'Calcolatore ROI Outsourcing', description: 'Strumento interattivo per calcolare i risparmi BPO' },
    pt: { label: 'Calculadora de ROI de Outsourcing', description: 'Ferramenta interativa para calcular economias de BPO' },
    nl: { label: 'Outsourcing ROI-calculator', description: 'Interactieve tool om uw BPO-besparingen te berekenen' },
    pl: { label: 'Kalkulator ROI Outsourcingu', description: 'Interaktywne narzędzie do obliczania oszczędności BPO' },
    ru: { label: 'Калькулятор ROI Аутсорсинга', description: 'Интерактивный инструмент для расчёта экономии BPO' },
    zh: { label: '外包 ROI 计算器', description: '用于计算 BPO 节省的交互式工具' },
    ja: { label: 'アウトソーシング ROI 計算ツール', description: 'BPOコスト削減を計算するインタラクティブツール' },
    ko: { label: '아웃소싱 ROI 계산기', description: 'BPO 절감액을 계산하는 인터랙티브 도구' },
    tr: { label: 'Dış Kaynak ROI Hesaplayıcı', description: 'BPO tasarrufunuzu hesaplamak için etkileşimli araç' },
    sv: { label: 'Outsourcing ROI-kalkylator', description: 'Interaktivt verktyg för att beräkna era BPO-besparingar' },
    da: { label: 'Outsourcing ROI-beregner', description: 'Interaktivt værktøj til at beregne dine BPO-besparelser' },
  },
};

// Helper: translate a link by looking up its href in the dictionary + translate country slugs
function translateLink(link: RelatedLink, lang: string): RelatedLink {
  const entry = linkDict[link.href];
  const translatedHref = translateCountryHref(link.href, lang);
  if (entry) {
    const loc = entry[lang] || entry.en;
    if (loc) return { href: translatedHref, label: loc.label, description: loc.description };
  }
  return { ...link, href: translatedHref };
}

const sectionTitle: Record<string, string> = {
  en: 'Explore More', fr: 'Explorer Plus', es: 'Explorar Más', ar: 'استكشف المزيد',
  de: 'Mehr Entdecken', it: 'Scopri di Più', pt: 'Explorar Mais', nl: 'Meer Ontdekken',
  pl: 'Odkryj Więcej', ru: 'Узнать Больше', zh: '探索更多', ja: 'もっと見る',
  tr: 'Daha Fazlasını Keşfedin', ko: '더 알아보기', sv: 'Utforska Mer', da: 'Udforsk Mere',
};
// Default hrefs shown when no custom links are passed
const defaultHrefs = ['/services/bpo', '/services/digital-studio', '/why-morocco', '/pricing', '/case-studies', '/contact'];

export function RelatedPagesSection({ links, variant = 'cards' }: Props) {
  const { language } = useLanguage();
  const title = sectionTitle[language] || sectionTitle.en;
  
  // If custom links passed, auto-translate them; otherwise build defaults from dictionary
  const resolvedLinks = links
    ? links.map(l => translateLink(l, language))
    : defaultHrefs.map(href => {
        const entry = linkDict[href];
        const loc = entry?.[language] || entry?.en;
        return { href, label: loc?.label || href, description: loc?.description || '' };
      });

  if (variant === 'compact') {
    return (
      <nav className="py-10 bg-muted/30" aria-label="Related pages">
        <div className="container mx-auto px-6">
          <h2 className="text-lg font-semibold text-foreground mb-4">{title}</h2>
          <div className="flex flex-wrap gap-3">
            {resolvedLinks.map((link, i) => (
              <Link
                key={i}
                to={`/${language}${link.href}`}
                className="text-sm text-primary hover:text-primary/80 underline underline-offset-2"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav className="py-16 bg-muted/20" aria-label="Related pages" itemScope itemType="https://schema.org/SiteNavigationElement">
      <div className="container mx-auto px-6">
        <h2 className="text-2xl font-bold text-foreground mb-8 text-center">{title}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
          {resolvedLinks.map((link, i) => (
            <Link
              key={i}
              to={`/${language}${link.href}`}
              className="group flex items-start gap-3 p-4 rounded-lg border border-border/50 hover:border-primary/30 hover:bg-primary/5 transition-colors"
              itemProp="url"
            >
              <div className="flex-1">
                <span className="font-semibold text-foreground group-hover:text-primary transition-colors" itemProp="name">
                  {link.label}
                </span>
                {link.description && (
                  <p className="text-sm text-muted-foreground mt-1" itemProp="description">{link.description}</p>
                )}
              </div>
              <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary mt-1 flex-shrink-0 transition-colors" aria-hidden="true" />
            </Link>
          ))}
        </div>
      </div>
    </nav>
  );
}
